import type { SalesOrdersTranslations } from './types';

/**
 * English translations for SalesOrders domain
 * Comprehensive translations covering all UI text
 */
export const enTranslations: SalesOrdersTranslations = {
  // Page/Section level
  title: 'Sales Orders',
  subtitle: 'Manage sales orders and contracts',
  overview: 'Overview',

  // Actions
  addSalesOrder: 'Add Sales Order',
  editSalesOrder: 'Edit Sales Order',
  deleteSalesOrder: 'Delete Sales Order',
  createSalesOrder: 'Create Sales Order',
  customer: 'Customer',
  selectCustomerToCreate:
    'Select a customer from the header before creating a sales order.',
  readOnlyAccess: 'Your role has read-only access to sales orders.',
  newSalesOrder: 'New Sales Order',
  reorder: 'Reorder',
  reorderTitle: 'Reorder Sales Order',
  reorderFrom: 'Reorder from {{orderNumber}}',
  placeReorder: 'Place Reorder',
  renew: 'Renew',
  cancel: 'Cancel',
  generatedOrdersAlert: 'Generated orders: {{count}}',
  confirmCreateSalesOrderTitle: 'Confirm sales order',
  confirmCreateSalesOrderDescription:
    'Review the generated fulfillment orders before creating the sales order.',
  generatedOrders: 'Generated Orders',
  requestedQuantity: 'Requested',
  droppedQuantity: 'Dropped',
  dropped: 'dropped',
  droppedOrdersSummary: '{{count}} dropped',
  details: 'Details',
  downloadPDF: 'Download PDF',
  viewOrders: 'View Orders',
  fillDetails: 'Fill in the details below',

  // Stats/Status labels
  draft: 'Draft',
  active: 'Active',
  expiring: 'Expiring',
  completed: 'Completed',
  total: 'Total',
  totalValue: 'Total Value',
  expiringSoon: 'Expiring Soon',
  expires: 'Expires',
  value: 'Value',
  consumption: 'Consumption',
  used: 'Used',
  orders: 'Orders',

  // Search and filters
  searchPlaceholder: 'Search sales orders...',
  noSalesOrders: 'No Sales Orders',
  noOrdersDescription:
    "You haven't created any sales orders yet. Create your first sales order to get started.",
  createFirst: 'Create Your First Sales Order',
  noItemsFound: 'No sales orders found',
  noItemsDescription: "You haven't added any sales orders yet.",
  addFirst: 'Add Your First Sales Order',
  filters: 'Filters',
  filtersTitle: 'Filter Sales Orders',
  filterByStatus: 'Filter by Status',
  filterByDeliveryAddress: 'Filter by Delivery Address',
  filterByProduct: 'Filter by Product',
  filterByValue: 'Filter by Value',
  filterByMinValue: 'Min Value',
  filterByMaxValue: 'Max Value',
  filterByCreatedDate: 'Filter by Created Date',
  filterByValidityDate: 'Filter by Validity Date',
  filterByAddress: 'Filter by Address',
  filterByFactory: 'Filter by Plant',
  noDeliveryAddresses: 'No delivery addresses available',
  noProductsAvailable: 'No products available',
  noFactoriesAvailable: 'No plants available',
  noFactories: 'No plants available',
  loadingFactories: 'Loading available plants...',
  factoryRequired: 'Please select a plant to continue',
  searchFactories: 'Search plants...',
  sorting: 'Sorting',
  sortBy: 'Sort By',
  sortOrder: 'Sort Order',
  searchProducts: 'Search products...',
  searchAddresses: 'Search addresses...',
  selectCreatedDateRange: 'Select created date range',
  selectValidityDateRange: 'Select validity date range',

  // Form fields
  basicInfo: 'Basic Information',
  startDate: 'Start Date',
  endDate: 'End Date',
  paymentMethod: 'Payment Method',
  city: 'City',
  selectCity: 'Select City',
  searchCity: 'Search city...',
  noCityFound: 'No city found',
  loadingCities: 'Loading available cities...',
  noCitiesForFactory: 'No delivery cities configured for this plant',
  cityRequired: 'City is required',
  selectCityFirst: 'Select city first',
  deliveryAddress: 'Delivery Address',
  selectedAddress: 'Selected Address',
  selectDeliveryAddress: 'Select Delivery Address',
  deliveryAddressRequired: 'Delivery address is required',
  deliveryAddressOptional: 'Delivery address is optional for now',
  orderLines: 'Order Lines',
  productType: 'Product Type',
  splitBy: 'Split By',
  qtyPerOrder: 'Qty per Order (Tons)',
  qtyPerOrderBags: 'Qty per Order (Bags)',
  numberOfOrders: 'Number of Orders',
  totalQuantity: 'Total',
  deliveryOptions: 'Delivery Options',
  automatic: 'Automatic',
  automaticTooltip:
    'The system will automatically assign available resources for delivery',
  exworksTooltip: 'Customer picks up from plant',
  assignedResource: 'Assigned Resource',
  selectResource: 'Select Resource',
  assignResource: 'Assign Resource',
  assignTooltip: 'Manually select a specific resource for this delivery',
  spccTooltip: 'South Gate will handle the delivery using their own fleet',
  resourcePlaceholder: 'Search and select a resource...',
  variantUnavailable: 'This product is not available in selected plant',
  waitNone: 'No wait now',
  waitMinutes: '~{{minutes}} min wait now',
  waitHours: '~{{hours}} h wait now',
  waitHoursMinutes: '~{{hours}} h {{minutes}} min wait now',
  waitHint: 'Estimated wait at the plant if a truck arrives now',
  bagWeightHint: 'Each bag weighs {{weight}} kg',
  quantityMinPlaceholder: 'Min {{value}}',
  quantityMinHint: 'Min: {{value}}',
  quantityMaxHint: 'Max: {{value}}',
  orderQuantityLimitsApplied: 'Order limits: {{min}} - {{max}}',
  orderQuantityMinLimitOnly: 'Order limits: {{min}}',

  // Order summary
  orderSummary: 'Order Summary',
  product: 'Product',
  lineTotal: 'Line Total',
  assigned: 'Assigned',
  grandTotal: 'Grand Total',
  notes: 'Notes',
  notesPlaceholder: 'Add any additional notes for this order...',
  subCustomerLabel: 'Sub-Customer',
  subCustomerSelectButton: 'Select Sub-Customer',
  subCustomerChangeButton: 'Change',
  subCustomerClearButton: 'Remove',
  subCustomerRequired: 'Select a sub-customer to create this sales order',
  createdBy: 'Created By',

  // Credit/Payment
  creditStatus: 'Credit Status',
  availableCredit: 'Available Credit',
  orderTotal: 'Order Total',
  creditExceededWarning:
    'Order total exceeds available credit. Consider adjusting the order or contacting support.',
  totalCreditLimit: 'Total Credit Limit',
  vibanNumber: 'VIBAN Number',
  vibanInfo: 'Transfer funds to this VIBAN for prepaid orders',
  savedCards: 'Saved Cards',
  addNewCard: 'Add New Card',
  newCard: 'New Card',
  useSavedCard: 'Use Saved Card',
  cardNumber: 'Card Number',
  expiry: 'Expiry',
  cvv: 'CVV',
  cardholderName: 'Cardholder Name',
  cardholderNamePlaceholder: 'Name on card',

  // Detail drawer
  salesOrderDetails: 'Sales Order Details',
  shipments: 'Shipments',
  noOrdersForContract: 'No orders for this contract yet',
  noShipmentsForContract: 'No shipments for this contract yet',
  multipleFactories: 'Multiple plants',
  factory: 'Plant',
  noFactoriesForCity: 'No plants serve this city',

  // Cancel dialog
  cancelConfirmTitle: 'Cancel Sales Order',
  cancelConfirmDescription:
    'Are you sure you want to cancel sales order {{name}}? This action cannot be undone.',

  // Form section (kept for compatibility)
  form: {
    name: 'Sales Order Name',
    namePlaceholder: 'Enter sales order name',
    status: 'Status',
    validation: {
      nameRequired: 'Sales order name is required',
    },
  },

  // Status (kept for compatibility)
  status: {
    draft: 'Draft',
    active: 'Active',
    expiring: 'Expiring',
    completed: 'Completed',
    canceled: 'Canceled',
    inactive: 'Inactive',
  },

  // Toast messages
  messages: {
    createSuccess: 'Sales order created successfully',
    updateSuccess: 'Sales order updated successfully',
    deleteSuccess: 'Sales order deleted successfully',
    deleteConfirm: 'Are you sure you want to delete this sales order?',
  },

  // Table headers
  table: {
    name: 'Name',
    status: 'Status',
    factory: 'Plant',
  },

  // Table column labels
  salesOrder: 'Sales Order',
  products: 'Products',
  period: 'Period',
  actions: 'Actions',
  canceled: 'Canceled',
  statusLabel: 'Status',

  // Sort options (additional)
  sortByDate: 'Created Date',
  sortNewest: 'Newest First',
  sortOldest: 'Oldest First',
  sortByOrderNumber: 'Order Number',
  sortByOrderNumberAsc: 'Order Number (A-Z)',
  sortByOrderNumberDesc: 'Order Number (Z-A)',
  sortByEndDate: 'End Date',
  sortByEndDateAsc: 'End Date (Earliest)',
  sortByEndDateDesc: 'End Date (Latest)',
  sortByStartDate: 'Start Date',
  sortByStartDateAsc: 'Start Date (Earliest)',
  sortByStartDateDesc: 'Start Date (Latest)',
  sortByValue: 'Value',
  sortByValueAsc: 'Value (Low to High)',
  sortByValueDesc: 'Value (High to Low)',
  sortByConsumption: 'Consumption',
  sortByConsumptionAsc: 'Consumption (Low to High)',
  sortByConsumptionDesc: 'Consumption (High to Low)',
  sortByRemaining: 'Remaining',
  sortByRemainingAsc: 'Remaining (Low to High)',
  sortByRemainingDesc: 'Remaining (High to Low)',
  sortByProductCount: 'Products Count',
  sortByProductCountAsc: 'Products (Fewest)',
  sortByProductCountDesc: 'Products (Most)',
  sortByStatus: 'Status',
  sortByStatusAsc: 'Status (A-Z)',
  sortByStatusDesc: 'Status (Z-A)',
  sortByCreatedAt: 'Created Date',
  sortByOrdersCount: 'Orders Count',
  sortByOrdersCountAsc: 'Orders Count (Least)',
  sortByOrdersCountDesc: 'Orders Count (Most)',
  sortOrderDesc: 'Descending',
  sortOrderAsc: 'Ascending',

  // Payment method labels
  creditLimit: 'Credit Limit',
  creditLimitDesc: 'Use your available credit',
  viban: 'VIBAN Transfer',
  vibanDesc: 'Transfer via Virtual IBAN',
  creditCard: 'Credit Card',
  creditCardDesc: 'Pay with credit card',

  // Top-level convenience keys
  name: 'Name',
  namePlaceholder: 'Enter sales order name',
  nameRequired: 'Sales order name is required',
  inactive: 'Inactive',
  createSuccess: 'Sales order created successfully',
  updateSuccess: 'Sales order updated successfully',
  deleteSuccess: 'Sales order deleted successfully',
  deleteConfirm: 'Are you sure you want to delete this sales order?',
  minValuePlaceholder: '> 0',
  maxValuePlaceholder: 'Any',

  errors: {
    deliveryAddressInvalidRef:
      'Delivery address is not a valid location reference',
    deliveryAddressNotFound:
      'Delivery address does not reference an existing location',
    deliveryAddressTenantMismatch:
      'Delivery address must belong to your account',
    deliveryAddressValidationFailed: 'Unable to validate delivery address',
    cityInvalidRef: 'City is not a valid reference',
    cityRequired: 'City is required',
    cityNotFound: 'City does not reference an existing city',
    cityValidationFailed: 'Unable to validate city',
    cityMismatch: 'City must match the delivery address city',
    missingDataPayload: 'Missing data payload in the request',
    salesOrderLinesRequired: 'At least one order line item is required',
    guestFieldsRequired: 'Guest orders require email and phone number',
    subCustomerRequired: 'A sub-customer is required for this customer',
    salesOrderLinesInvalidFormat: 'Order lines must be an array of objects',
    lineCommittedQuantityRequired:
      'Committed quantity is required for each line',
    lineVariantRequired: 'Product variant is required for each line',
    lineSplitByRequired: 'Split by is required for each line',
    documentIdRequired: 'Document ID is required',
    notFound: 'Sales order not found',
  },

  export: {
    exportingTitle: 'Exporting...',
    exportingDesc: 'Fetching all records',
    exportFailedTitle: 'Export failed',
    exportFailed: 'Could not export sales orders',
    emailPromptTitle: 'Where should we send the export?',
    emailPromptDescription: 'Enter the email address for this export. Small exports download immediately after submit, and larger exports are sent by email.',
    emailLabel: 'Email address',
    emailPlaceholder: 'name@example.com',
    emailRequired: 'Email is required',
    emailInvalid: 'Please enter a valid email address',
    exportDownloadedTitle: 'Export downloaded',
    exportDownloadedDescription: 'Downloaded {{count}} sales orders',
    exportEmailedTitle: 'Export will be emailed',
    exportEmailedDescription: 'We will send {{count}} sales orders to {{email}}',
  },
};
